/**
 * A failed source, or one without a file: why, and the two ways out — upload the file
 * (name, publisher and link are kept) or edit the source to fix its link.
 */
import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { AlertTriangle, Upload, Pencil } from 'lucide-react';
import { Button, Actions, StatusLine } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { uploadSourceFile, errorKey } from './datasetApi';

const Box = styled.div`
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$failed ? 'rgba(248, 113, 113, 0.45)' : 'rgba(255, 255, 255, 0.15)')};
  background: ${(p) => (p.$failed ? 'rgba(248, 113, 113, 0.08)' : 'rgba(255, 255, 255, 0.04)')};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Heading = styled.strong`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${(p) => (p.$failed ? '#fca5a5' : '#fff')};
`;

const Help = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #ccc;
`;

const Raw = styled.details`
  font-size: 12px;
  color: #999;
  summary { cursor: pointer; }
  pre { white-space: pre-wrap; word-break: break-word; margin: 6px 0 0; }
`;

export default function FixSourcePanel({ datasetId, source, onEdit, onChanged }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const failed = source.status === 'failed';
  const blocked = source.metadata?.errorCode === 'download_blocked';
  const help = !failed
    ? t('datasetWorkspace.awaitingHelp')
    : blocked ? t('datasetWorkspace.blockedHelp') : t('datasetWorkspace.failedHelp');

  const handleUpload = async () => {
    setBusy(true);
    setError(null);
    try {
      await uploadSourceFile(datasetId, source.id, file);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box $failed={failed}>
      <Heading $failed={failed}>
        <AlertTriangle size={16} />
        {failed ? t(errorKey(source)) : t('datasetWorkspace.fixTitle')}
      </Heading>
      <Help>{help}</Help>
      <Actions>
        <input ref={fileRef} type="file" accept=".pdf,.txt,.md,.json,application/pdf"
          aria-label={t('datasetWorkspace.uploadFile')}
          onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ color: '#ccc' }} />
        <Button $size="small" $variant="primary" disabled={!file || busy} onClick={handleUpload}>
          <Upload /> {busy ? t('datasetWorkspace.uploading') : t(source.has_file ? 'datasetWorkspace.replaceFile' : 'datasetWorkspace.uploadFile')}
        </Button>
        <Button $size="small" onClick={onEdit}><Pencil /> {t('datasetWorkspace.edit')}</Button>
        {error && <StatusLine $error>{error}</StatusLine>}
      </Actions>
      {source.error && (
        <Raw>
          <summary>{t('datasetWorkspace.errorDetails')}</summary>
          <pre>{source.error}</pre>
        </Raw>
      )}
    </Box>
  );
}
